import { useFlags } from 'launchdarkly-react-client-sdk';
import { ReactNode } from 'react';
import { Inline } from '../../../../design-system/inline/inline';
import { TabsList, Tabs as TabsRoot, TabsTrigger } from '../../../../design-system/tabs/tabs-primitives';
import { exhaustiveCheck } from '../../../../utils/exhaustive-check';
import { TabCounter } from './tab-counter';

export type TabValue = 'TO_PAY' | 'REVIEW_MATCHES' | 'COMPLETED';

type TabCounterConfig =
  | {
      showCounter: false;
    }
  | {
      showCounter: true;
      count: number;
    };

export type TabTrigger = {
  value: TabValue;
  counterConfig: TabCounterConfig;
};

type InvoicesSectionTabsProps = {
  triggers: TabTrigger[];
  children: ReactNode;
};

export const InvoicesSectionTabs = ({ triggers, children }: InvoicesSectionTabsProps) => {
  const { isReviewMatchLive } = useFlags();

  return (
    <TabsRoot defaultValue="TO_PAY">
      <TabsList className="rounded-none px-0">
        <Inline space={8}>
          {triggers.map((trigger) => {
            const tabTriggerText = getTabTriggerText(trigger.value, isReviewMatchLive);

            return (
              <TabsTrigger
                key={trigger.value}
                value={trigger.value}
                className="rounded-none px-0 py-0 text-seapoint-gray outline-offset-8 data-[state=active]:bg-white"
              >
                <Inline space={2}>
                  <p className="border-b-2 border-b-transparent text-base font-medium leading-6 tracking-tight text-seapoint-gray group-data-[state=active]:border-b-seapoint-yellow group-data-[state=active]:text-black">
                    {tabTriggerText}
                  </p>

                  {trigger.counterConfig.showCounter && <TabCounter count={trigger.counterConfig.count} />}
                </Inline>
              </TabsTrigger>
            );
          })}
        </Inline>
      </TabsList>

      {children}
    </TabsRoot>
  );
};

const getTabTriggerText = (tabValue: TabValue, isReviewMatchLive: boolean) => {
  switch (tabValue) {
    case 'TO_PAY':
      return 'To Pay';
    case 'REVIEW_MATCHES':
      return 'Review Matches';
    case 'COMPLETED':
      return isReviewMatchLive ? 'Completed' : 'Paid';
    default:
      return exhaustiveCheck(tabValue);
  }
};
